import capitalize from 'lodash/capitalize';
import get from 'lodash/get';

import API from '@wetransfer/transfer/api';
import { checkIfSSOIsEnabled } from '@wetransfer/transfer/auth-bundle/helpers/sso';
import { logout } from '@wetransfer/transfer/auth-bundle/store/actions';
import { PLAN_TIER } from '@wetransfer/transfer/constant/plan-tier';
import { AppConfig } from '@wetransfer/transfer/core-bundle/config/app-config';
import Helper from '@wetransfer/transfer/helper';
import Analytics from '@wetransfer/transfer/modules/analytics';
import Browser from '@wetransfer/transfer/modules/concorde-browser';
import Cookie from '@wetransfer/transfer/modules/concorde-cookie';
import { HttpResponseCodes } from '@wetransfer/transfer/modules/http/axios/response';
import I18n from '@wetransfer/transfer/modules/i18n';
import Translations from '@wetransfer/transfer/modules/i18n/dictionary';
import { countryHelpers } from '@wetransfer/transfer/modules/i18n/helper';
import ActionHelper from '@wetransfer/transfer/modules/redux-actions';
import Router from '@wetransfer/transfer/modules/router';
import Account from '@wetransfer/transfer/store/action/account';
import Errors from '@wetransfer/transfer/store/action/errors';
import Notification from '@wetransfer/transfer/store/action/notification';
import Pending from '@wetransfer/transfer/store/action/pending';
import Transfer from '@wetransfer/transfer/store/action/transfer';
import { getChannel } from '@wetransfer/transfer/store/helper/channel';
import { TRANSFER_STATES } from '@wetransfer/transfer/transfer-bundle/constant/transfer-states';
import Transform from '@wetransfer/transfer/transformers';

import SessionActions from './session';

export const actions = ActionHelper.types([
  'GET_USER',
  'CHANGE_USER_LANGUAGE',
  'CHANGE_USER_IS_VERIFIED',
  'UPDATE_USER_EMAIL',
  'REQUEST_PASSWORD_RESET_SUCCESS',
  'UPDATE_ONBOARDED_PREFERENCE',
  'CHANGE_FORCE_VERIFICATION_ENABLED',
  'CHANGE_HAS_SKIPPED_VERIFICATION',
  'DISABLE_OTP',
]);

const userActions = ActionHelper.generate({
  updateUserEmail: [actions.UPDATE_USER_EMAIL, 'email'],
  updateOnboardedPreference: [actions.UPDATE_ONBOARDED_PREFERENCE, 'value'],
  disableOtp: [actions.DISABLE_OTP],

  setWTPlusCookie({ domain, planTier }) {
    // TODO: Temporary conditional for other tiers
    const isPaidTier =
      planTier === PLAN_TIER.PLUS ||
      planTier === PLAN_TIER.PREMIUM_2022 ||
      planTier === PLAN_TIER.PRO_2022;
    const value = isPaidTier ? 1 : 0;
    Cookie.set(AppConfig.cookies.plus.name, value, {
      domain,
      days: 365,
      path: '/',
    });
  },

  unsetWTPlusCookie({ domain }) {
    Cookie.unset(AppConfig.cookies.plus.name, { domain, path: '/' });
  },

  pushSigninAnalytics(dispatch, id, rememberMe = false) {
    Analytics.configure({ userId: id });
    Analytics.trackGoogleEvent({
      category: 'Plus',
      action: 'Signin',
      label: rememberMe,
      user: {
        user_id: id,
        account_type: 'plus',
        frequency_type: Helper.getUserFrequency(),
      },
    });

    dispatch(SessionActions.acceptTermsConditions()); // NOTE: User accepted this when signing up
  },

  pushSignoutAnalytics() {
    Analytics.trackGoogleEvent({
      category: 'Plus',
      action: 'Signout',
      user: {
        user_id: undefined,
        account_type: 'free',
      },
    });

    Analytics.trackSnowplowEvent({
      category: 'plus_account',
      action: 'signout',
    });
  },

  pushSignupAnalytics(dispatch, id, billing, tier, trk) {
    // This fires prior to the signup completing; it tracks intent to sign up, but includes users
    // who drop out of the funnel (eg at payment verification)
    Analytics.trackGoogleEvent({
      category: capitalize(tier),
      action: 'Signup',
      label: billing,
      user: {
        user_id: id,
        has_transacted: false,
        subscription_type: billing,
        upload_count: 0,
        download_count: 0,
        frequency_type: Helper.getUserFrequency(),
      },
      channel: trk || undefined,
    });

    userActions.pushSigninAnalytics(dispatch, id);
  },

  getNextPage(transfer, redirect, fallbackRoute) {
    // Users should be redirected to the download page of the transfer they were visiting
    const prevRoute = Router.getLastRoute('transfer', '/');

    if (
      transfer &&
      transfer.id &&
      transfer.items.length > 0 &&
      prevRoute &&
      prevRoute.includes('downloads')
    ) {
      const { id, recipient_id, secret } = transfer;
      const url = ['/downloads', id, recipient_id].filter(Boolean).join('/');

      // If there is no secret we want `undefined` so that the page shows Transfer expired rather than returning a 404.
      // This prevents malicious users guessing transfer urls.
      return `${url}/${secret}`;
    }

    return redirect || fallbackRoute;
  },

  redirectWithRefresh({
    session,
    route,
    account,
    nextPage,
    keepParams = true,
    keepSubDomain = true,
  }) {
    // Prevents "are you sure" browser modal from happening.
    window.onbeforeunload = null;

    const { subdomain_name, active_subscription } = account;

    const subDomain =
      active_subscription && subdomain_name && keepSubDomain
        ? subdomain_name
        : null;

    const fullDomain = subDomain
      ? `${subDomain}.${session.domain}`
      : session.domain;

    const url = new URL(
      nextPage,
      `${location.protocol}//${fullDomain}:${location.port}`
    );

    if (keepParams) {
      Object.entries(route.query).forEach(([key, value]) => {
        url.searchParams.set(key, value);
      });
    }

    if (route.segment) {
      url.hash = route.segment;
    }
    location.assign(url.href); // Hard refresh to ensure state and tracking is fully updated
  },

  delayedResetPending(dispatch, pendingId) {
    //This ensures that submit buttons stay inactive until after the user has been redirected
    setTimeout(
      () => dispatch(Pending.resetPending(pendingId)),
      AppConfig.actions.extended_pending
    );
  },

  signin({ email, password, rememberMe, otpToken }) {
    const pendingId = 'signin';

    return async (dispatch, getState) => {
      // Don't even request if it's empty.
      if (!email || !password) {
        return dispatch(
          Notification.setNotification('error', 'signin_no_data_exception')
        );
      }

      dispatch(Pending.setPending(pendingId));

      try {
        const response = await API.User.createSession({
          email,
          password,
          remember_me: rememberMe,
          otp_token: otpToken,
        });

        const { user, account } = Transform.auth(response.body);
        const { transfer, session, route } = getState();

        userActions.pushSigninAnalytics(dispatch, user.id, rememberMe);
        userActions.delayedResetPending(dispatch, pendingId);

        const nextPage = userActions.getNextPage(
          transfer,
          session.redirect,
          '/transfers'
        );
        userActions.redirectWithRefresh({ session, route, account, nextPage });
      } catch (error) {
        const errorMessage = error.body.message;

        if (errorMessage === 'require_otp_verification') {
          dispatch(Pending.resetPending(pendingId));
          throw new Error('RequireOTPVerification');
        }

        switch (errorMessage) {
          case 'invalid_email_or_password':
            dispatch(
              Notification.setNotification(
                'error',
                'signin_invalid_credentials'
              )
            );
            break;
          case 'otp_token_invalid':
            dispatch(
              Notification.setNotification('error', 'otp_token_invalid')
            );
            break;
          default:
            dispatch(Notification.setNotification('error', 'signin_exception'));
            break;
        }

        dispatch(Errors.setError('signin', errorMessage));
        dispatch(Pending.resetPending(pendingId));
      }
    };
  },

  signout({ redirectTo, isForwardedTransfer } = {}) {
    const pendingId = 'signout';

    return async (dispatch, getState) => {
      dispatch(Pending.setPending(pendingId));

      try {
        const { session, route, account } = getState();

        // Is there a forward pending? Replace it with a normal transfer.
        if (isForwardedTransfer) {
          dispatch(Transfer.create());
        }

        userActions.pushSignoutAnalytics();

        // Set sign out cookie with 30 minute expiration
        const expiryDate = new Date(Date.now() + 60 * 30 * 1000);
        Cookie.set(AppConfig.cookies.signout.name, 1, { expires: expiryDate });

        userActions.delayedResetPending(dispatch, pendingId);

        const nextPage = redirectTo
          ? redirectTo
          : Router.getLastRoute('transfer', '/');

        if (checkIfSSOIsEnabled()) {
          await dispatch(logout());

          return;
        }

        userActions.redirectWithRefresh({
          session,
          route,
          account,
          nextPage,
          keepSubDomain: false,
        });
      } catch (error) {
        dispatch(Notification.setNotification('error', 'signout_exception'));
        dispatch(Pending.resetPending(pendingId));
      }
    };
  },

  signup(params = {}, tracking = {}, formName = 'signup', subscriptionId) {
    const pendingId = formName;

    return async (dispatch, getState) => {
      dispatch(Pending.setPending(pendingId));

      // Remove vat if it only has the prefix
      const data = { ...params, ...tracking };
      const country = countryHelpers.getCountryByCode(data.country_code);

      if (data.vat_number === country?.vatPrefix) {
        data.vat_number = '';
      }

      try {
        const response =
          formName === 'upgrade'
            ? await API.Account.upgradeSubscription(data, {
                pathExtValues: { subscriptionId },
              })
            : await API.User.signup(data);

        const { checkout: { order } = {} } = response.body;
        const { user, account } = Transform.auth(response.body.user);
        const { session, route } = getState();

        if (response.body.order_completed && response.body.redirect_to_url) {
          // A real redirect, since the session will be replaced.
          const url = new URL(response.body.redirect_to_url);
          location.assign(url.href);
        }

        //Initialise record of sent and downloaded transfers
        Cookie.set('wt_sent', 0, { days: 365 });
        Cookie.set('wt_downloaded', 0, { days: 365 });

        userActions.pushSignupAnalytics(
          dispatch,
          user.id,
          params.billing,
          data.planTier,
          data.trk
        );
        userActions.delayedResetPending(dispatch, pendingId);

        // A payment might not be required if the order total is zero
        if (order && order.skip_payment === true) {
          userActions.redirectWithRefresh({
            session,
            route,
            account,
            nextPage: '/payment/pending?skip=true',
            keepParams: false,
          });

          return;
        }

        if (checkIfSSOIsEnabled()) {
          // With SSO we're not refreshing the page anymore, so
          // after upgrading the account, we should fetch the user
          // and the new upgraded subscription so the user has tier
          // plus and the subscription is active.
          await Promise.all([
            dispatch(userActions.getUser()),
            dispatch(
              Account.getSubscription(account.id, account.subscription.id)
            ),
          ]);

          Router.navigateTo('/payment/methods', true);

          return;
        }

        userActions.redirectWithRefresh({
          session,
          route,
          account,
          nextPage: '/payment/methods',
        });
      } catch (error) {
        if (error && error.body && error.body.fields) {
          dispatch(Errors.setError('signup', error.body.fields));
        }
        dispatch(Notification.setNotification('error', 'signup_account_error'));
        dispatch(Pending.resetPending(pendingId));
      }
    };
  },

  getUser() {
    const pendingId = 'getUser';

    return async (dispatch, getState) => {
      dispatch(Pending.setPending(pendingId));

      try {
        const response = await API.User.getCurrentUser();
        const pictureFromAuth0 = getState().auth?.picture || '';
        const channel = getChannel() || {};

        // https://auth0.com/rules/default-picture-null-avatars
        const hasSocialProviderPicture =
          pictureFromAuth0 && !pictureFromAuth0.includes('cdn.auth0.com');

        const fallbackImage = hasSocialProviderPicture
          ? pictureFromAuth0
          : Helper.getAvatar(response.body.id);

        if (channel.name) {
          const resChannelLimitation =
            await API.User.getChannelTransferSizeLimit(null, {
              pathExtValues: {
                name: channel.name,
              },
            });

          response.body.rules.transfer_limit_for_default_recipient =
            resChannelLimitation.body.transfer_limit_for_default_recipient;
        }

        dispatch({
          type: actions.GET_USER,
          details: {
            ...response.body,
            has_profile_picture: Boolean(response.body.profile_picture),
            profile_picture: response.body.profile_picture
              ? response.body.profile_picture
              : {
                  image_url: fallbackImage,
                  thumbnail_url: fallbackImage,
                },
          },
        });

        dispatch(Pending.resetPending(pendingId));

        return response.body;
      } catch (error) {
        dispatch(
          Notification.setNotification('error', 'retrieve_user_exception')
        );

        dispatch(Pending.resetPending(pendingId));
      }
    };
  },

  updateUser(params = {}) {
    const pendingId = 'account';

    return async (dispatch) => {
      dispatch(Pending.setPending(pendingId));

      try {
        const { profile_picture, ...data } = params;

        const response = await API.User.updateUser({
          ...data,
          // only include profile_picture if its not a default
          profile_picture: profile_picture?.id ? profile_picture : null,
        });

        dispatch({
          type: actions.GET_USER,
          details: response.body,
        });
        dispatch(Notification.setNotification('notification', 'update_user'));
        dispatch(Pending.resetPending(pendingId));
      } catch (error) {
        if (error.status === HttpResponseCodes.BAD_REQUEST) {
          dispatch(Errors.setError('account', error.body.fields));
          dispatch(
            Notification.setNotification('error', 'something_went_wrong')
          );
        } else {
          dispatch(Notification.setNotification('error', 'update_user_error'));
        }
        dispatch(Pending.resetPending(pendingId));
        throw error;
      }
    };
  },

  updateUserLanguage(language) {
    const pendingId = 'updateUserLanguage';

    return async (dispatch) => {
      dispatch(Pending.setPending(pendingId));

      try {
        const response = await API.User.updateUserLanguage(
          { language },
          {
            pathExtValues: {
              language,
            },
          }
        );

        // Update the dictionary
        Translations.update(language, response.body);

        dispatch({
          type: actions.CHANGE_USER_LANGUAGE,
          language,
        });
      } catch (error) {
        dispatch(Notification.setNotification('error', 'something_went_wrong'));
      }

      dispatch(Pending.resetPending(pendingId));
    };
  },

  updatePassword(params = {}) {
    const pendingId = 'password';

    return async (dispatch) => {
      dispatch(Pending.setPending(pendingId));

      try {
        await API.User.updatePassword(params);

        dispatch(
          Notification.setNotification('notification', 'update_password')
        );
      } catch (error) {
        if (error.status === HttpResponseCodes.BAD_REQUEST) {
          dispatch(Errors.setError('password', error.body.fields));
        } else {
          dispatch(
            Notification.setNotification('error', 'update_password_exception')
          );
        }
      }

      dispatch(Pending.resetPending(pendingId));
    };
  },

  requestPasswordReset(email) {
    const pendingId = 'requestPasswordReset';

    return async (dispatch) => {
      dispatch(Pending.setPending(pendingId));

      try {
        const response = await API.User.requestPasswordReset({
          email,
        });

        dispatch({
          type: actions.REQUEST_PASSWORD_RESET_SUCCESS,
          details: response.body,
        });
      } catch (error) {
        if (error.status === HttpResponseCodes.NOT_FOUND) {
          dispatch(Errors.setError('requestPasswordReset', error.body.message));
          dispatch(
            Notification.setNotification(
              'error',
              'request_password_reset_no_user'
            )
          );
        } else {
          dispatch(
            Errors.setError(
              'requestPasswordReset',
              'request_password_reset_exception'
            )
          );
          dispatch(
            Notification.setNotification(
              'error',
              'request_password_reset_exception'
            )
          );
        }
      }

      dispatch(Pending.resetPending(pendingId));
    };
  },

  resetPassword(params = {}) {
    const pendingId = 'password';

    return async (dispatch) => {
      dispatch(Pending.setPending(pendingId));

      try {
        await API.User.resetPassword(params);

        Router.navigateTo('/log-in');
      } catch (error) {
        if (error.status === HttpResponseCodes.BAD_REQUEST) {
          if (error.body.fields && error.body.fields.reset_password_token) {
            dispatch(
              Notification.setNotification(
                'error',
                'reset_password_token_invalid'
              )
            );
          } else {
            dispatch(
              Notification.setNotification('error', 'reset_password_error')
            );
          }
        } else {
          dispatch(Errors.setError('password', 'reset_password_exception'));
          dispatch(
            Notification.setNotification('error', 'reset_password_exception')
          );
        }
        dispatch(Pending.resetPending(pendingId));
      }
    };
  },

  unsubscribeOnboarding(verifierId) {
    const pendingId = 'unsubscribeOnboarding';

    return async (dispatch) => {
      dispatch(Pending.setPending(pendingId));

      try {
        await API.User.unsubscribeUserOnboarding({ verifier_id: verifierId });
      } finally {
        dispatch(Pending.resetPending(pendingId));
      }
    };
  },

  unsubscribeLinkTransfer(verifierId) {
    const pendingId = 'unsubscribeLinkTransfer';

    return async (dispatch) => {
      dispatch(Pending.setPending(pendingId));

      try {
        await API.User.unsubscribeLinkTransfer({ verifier_id: verifierId });
      } finally {
        dispatch(Pending.resetPending(pendingId));
      }
    };
  },

  verifyAccountEmail({ verificationCode, verificationSignature }) {
    return async (dispatch) => {
      try {
        await API.User.verifyAccountEmail({
          verification_code: verificationCode,
          verification_signature: verificationSignature,
        });
        dispatch(Transfer.setState(TRANSFER_STATES.VERIFY_ACCOUNT_SUCCESS));
        dispatch({
          type: actions.CHANGE_USER_IS_VERIFIED,
          isVerified: true,
        });
        dispatch({
          type: actions.CHANGE_HAS_SKIPPED_VERIFICATION,
          hasSkippedVerification: false,
        });
      } catch (error) {
        switch (error.status) {
          case HttpResponseCodes.TOO_MANY_REQUESTS:
            dispatch(
              Notification.pushTransfer({
                text: 'AccountEmailVerificationLinkResendThrottleError',
              })
            );
        }
        throw error;
      }
    };
  },

  resendVerifyAccountEmail({ email, showTransferNotification }) {
    return async (dispatch, getState) => {
      try {
        await API.User.validate({ email });
        await API.User.resendVerifyAccountEmail({ email });
        const { route } = getState();

        if (
          route.path === '/account-verification' &&
          Boolean(route.query.sig)
        ) {
          const navigateTo = Browser.isMobile ? '/upload' : '/';
          Router.navigateTo(navigateTo, false, false, { query: {} });
        }
        dispatch(userActions.updateUserEmail(email));
        dispatch(Account.updateAccountMembershipUserEmail(email));
        dispatch(Transfer.setState(TRANSFER_STATES.VERIFY_ACCOUNT));
        dispatch({
          type: actions.CHANGE_FORCE_VERIFICATION_ENABLED,
          forceVerificationEnabled: false,
        });
      } catch (error) {
        switch (error.status) {
          case HttpResponseCodes.TOO_MANY_REQUESTS: {
            if (showTransferNotification) {
              dispatch(
                Notification.pushTransfer({
                  text: 'AccountEmailVerificationLinkResendThrottleError',
                })
              );
            }

            throw error;
          }
          default:
            if (get(error, 'body.fields.email', []).includes('taken')) {
              dispatch(
                Notification.pushTransfer({
                  text: 'AccountEmailVerificationResendEmailExistsError',
                })
              );
            } else {
              dispatch(
                Notification.pushTransfer({
                  text: 'AccountEmailVerificationResendError',
                })
              );
            }
            throw error;
        }
      }
    };
  },

  setUserAsOnboarded() {
    return async (dispatch) => {
      await API.User.setUserAsOnboarded();
      dispatch(userActions.updateOnboardedPreference(true));
    };
  },

  redirectToTwoFactorSSOSetup() {
    return async (dispatch) => {
      try {
        const {
          body: { ticket_url },
        } = await API.User.retrieveEnableTwoFactorRedirectUrl();
        location.assign(ticket_url);
      } catch (error) {
        if (error.status === HttpResponseCodes.TOO_MANY_REQUESTS) {
          dispatch(
            Notification.setNotification(
              'error',
              'update_information_throttle',
              {
                throttle_time: I18n.t('datetime.distance_in_words.x_minutes', {
                  count: 10,
                }),
              }
            )
          );
        } else {
          Notification.setNotification('error', 'something_went_wrong');
        }
        throw error;
      }
    };
  },

  disableTwoFactorAuthentication(password, type = 'otp') {
    return async (dispatch) => {
      try {
        await API.User.disableTwoFactorAuthentication(
          { password },
          {
            pathExtValues: {
              type,
            },
          }
        );
        dispatch(userActions.disableOtp());
      } catch (error) {
        if (error.status === HttpResponseCodes.TOO_MANY_REQUESTS) {
          return dispatch(
            Notification.setNotification(
              'error',
              'update_information_throttle',
              {
                throttle_time: I18n.t('datetime.distance_in_words.x_minutes', {
                  count: 10,
                }),
              }
            )
          );
        }

        if (get(error, 'body.error') === 'invalid_password') {
          return dispatch(
            Notification.setNotification('error', 'invalid_password')
          );
        }

        dispatch(Notification.setNotification('error', 'something_went_wrong'));
      }
    };
  },

  unsubscribeFromExpirationReminders({ signature, isUser, email, emailType }) {
    return async (dispatch) => {
      try {
        if (isUser) {
          await API.User.unsubscribeFromExpirationReminders({
            signature,
            email_type: emailType,
          });

          return;
        }

        await API.Reminder.unsubscribeExpiration({
          signature,
          email,
          email_type: emailType,
        });
      } catch (error) {
        if (error.status === HttpResponseCodes.FORBIDDEN) {
          dispatch(
            Notification.setNotification(
              'error',
              'unsubscribe_expiration_invalid_signature'
            )
          );
        } else {
          dispatch(
            Notification.setNotification(
              'error',
              'unsubscribe_expiration_exception'
            )
          );
        }
        throw error;
      }
    };
  },

  subscribeToMarketingEmails(optedIn) {
    const pendingId = 'subscribeToMarketingEmails';

    return async (dispatch) => {
      dispatch(Pending.setPending(pendingId));

      try {
        await API.User.setMarketingEmailPreference({
          email_marketing_opted_in: optedIn,
        });

        // Update user details
        dispatch(userActions.getUser());
      } catch {
        // Silently fail
      }

      dispatch(Pending.resetPending(pendingId));

      dispatch(Transfer.create());
    };
  },

  getUserOccupation() {
    return async () => {
      const response = await API.User.getUserOccupation();

      return response.body;
    };
  },

  updateUserDataOptIn(dataOptIn) {
    const pendingId = 'userOccupationValueUpdate';

    return async (dispatch) => {
      dispatch(Pending.setPending(pendingId));

      try {
        await API.User.updateUserDataOptIn({ data_opt_in: dataOptIn });
        // Update user details
        dispatch(userActions.getUser());
      } catch {
        // Silently fail
      }

      dispatch(Pending.resetPending(pendingId));
    };
  },

  setUserOccupation(value) {
    const pendingId = 'userOccupationValue';

    return async (dispatch) => {
      dispatch(Pending.setPending(pendingId));

      try {
        await API.User.setUserOccupation(value);

        // Update user details
        dispatch(userActions.getUser());
      } catch {
        // Silently fail
      }

      dispatch(Pending.resetPending(pendingId));
    };
  },

  getOnboardingStepValue() {
    return async () => {
      const response = await API.User.getOnboardingStepValue();
      const eventsList = response.body.events;

      return eventsList[eventsList.length - 1];
    };
  },

  setOnboardingStepValue(step) {
    const pendingId = 'onboardingStepValue';

    return async (dispatch) => {
      dispatch(Pending.setPending(pendingId));

      try {
        const lastOnboardingStep = await API.User.getOnboardingStepValue();
        const lastEventsList = lastOnboardingStep.body.events;

        if (step !== lastEventsList[lastEventsList.length - 1]) {
          await API.User.setOnboardingStepValue({
            event: step,
          });
        }

        // Update user details
        dispatch(userActions.getUser());
      } catch {
        // Silently fail
      }

      dispatch(Pending.resetPending(pendingId));
    };
  },
});

export default userActions;
